import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { toast } from 'sonner';
import { Calendar, Loader2, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useTurnstile } from '../../components/TurnstileCaptcha';

export const Login = () => {
  const { t, isRTL } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  // CAPTCHA
  const { token: captchaToken, isVerified, TurnstileComponent } = useTurnstile();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check CAPTCHA (skip in development/test mode)
    const skipCaptcha = process.env.REACT_APP_TURNSTILE_SITE_KEY === '1x00000000000000000000AA';
    if (!skipCaptcha && !isVerified) {
      toast.error('Veuillez compléter la vérification CAPTCHA');
      return;
    }
    
    setLoading(true);
    
    try {
      const user = await login(formData.email, formData.password, captchaToken);
      toast.success(t('success'));
      
      // Redirect based on role
      if (user.role === 'super_admin') {
        navigate('/admin');
      } else if (user.role === 'country_admin') {
        navigate('/country-admin');
      } else if (user.role === 'professional') {
        navigate('/professional');
      } else if (user.role === 'medical_rep') {
        navigate('/medical-rep/dashboard');
      } else if (user.role === 'deputy_admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12 pb-24" data-testid="login-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Link to="/" className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-2xl text-slate-900">drbooking</span>
          </Link>
          <CardTitle className="text-2xl">{t('login_title')}</CardTitle>
          <CardDescription>{t('hero_subtitle').substring(0, 60)}...</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="email@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="login-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="login-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <Link 
                to="/forgot-password" 
                className="text-sm text-primary hover:underline"
                data-testid="forgot-password-link"
              >
                Mot de passe oublié ?
              </Link>
            </div>
            
            {/* CAPTCHA */}
            <TurnstileComponent />
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 me-2 animate-spin" />
                  {t('loading')}
                </>
              ) : (
                t('login_button')
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              {t('no_account')}{' '}
              <Link to="/register" className="text-primary font-medium hover:underline" data-testid="goto-register">
                {t('nav_register')}
              </Link>
            </p>
          </div>

          <div className="mt-4">
            <Link 
              to="/" 
              className={`flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-primary ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <ArrowLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
              {t('back')}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
